import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SponsorBanner = ({ interests = [], page = 'unknown', size = 'medium' }) => {
  const [sponsor, setSponsor] = useState(null);

  useEffect(() => {
    fetchAd();
  }, []);

  const fetchAd = async () => {
    try {
      const interestStr = interests.join(',');
      const response = await axios.get(`${API}/sponsors/ads/random`, {
        params: { interests: interestStr, page }
      });
      if (response.data) {
        setSponsor(response.data);
      }
    } catch (error) {
      console.log('No ads available');
    }
  };

  if (!sponsor) return null;

  const sizeClasses = {
    small: 'h-20',
    medium: 'h-32',
    large: 'h-48'
  };

  return (
    <a 
      href={sponsor.ad_link || sponsor.website_url} 
      target="_blank" 
      rel="noopener noreferrer sponsored"
      className={`block relative overflow-hidden rounded-lg bg-muted ${sizeClasses[size]} group`}
    >
      {sponsor.ad_image_url ? (
        <img 
          src={sponsor.ad_image_url} 
          alt={sponsor.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-r from-primary/20 to-secondary/20 flex items-center justify-center">
          <span className="text-2xl font-heading">{sponsor.name}</span>
        </div>
      )}
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex flex-col justify-end p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white text-sm font-medium">{sponsor.ad_text || sponsor.name}</p>
            <span className="text-white/70 text-xs">Sponsored by {sponsor.name}</span>
          </div>
          <ExternalLink className="w-4 h-4 text-white/70" />
        </div>
      </div>
      
      {/* Sponsor badge */}
      <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
        Ad
      </div>
    </a>
  );
};

export const SponsorSidebar = ({ interests = [], page = 'sidebar' }) => {
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    try {
      const response = await axios.get(`${API}/sponsors`, {
        params: { active_only: true }
      });
      setSponsors(response.data.slice(0, 3));
    } catch (error) {
      console.log('No sponsors available');
    }
  };

  if (sponsors.length === 0) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">Our Sponsors</h3>
        <span className="text-xs text-muted-foreground">Ad</span>
      </div>
      
      {sponsors.map((sponsor) => (
        <a
          key={sponsor.id}
          href={sponsor.website_url}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="block p-3 rounded-lg border hover:bg-muted transition-colors"
        >
          <div className="flex items-center gap-3">
            {sponsor.logo_url ? (
              <img 
                src={sponsor.logo_url} 
                alt={sponsor.name}
                className="w-10 h-10 rounded object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
                <span className="font-bold text-primary">{sponsor.name.charAt(0)}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{sponsor.name}</p>
              <p className="text-xs text-muted-foreground truncate">{sponsor.description}</p>
            </div>
          </div>
        </a>
      ))}
      
      <p className="text-xs text-muted-foreground text-center">
        Sponsors help keep AADC free
      </p>
    </div>
  );
};

export const SponsorCard = ({ sponsor }) => {
  const levelColors = {
    platinum: 'bg-gradient-to-r from-slate-200 to-slate-400 text-slate-800',
    gold: 'bg-gradient-to-r from-amber-200 to-amber-400 text-amber-800',
    silver: 'bg-gradient-to-r from-gray-200 to-gray-400 text-gray-800',
    bronze: 'bg-gradient-to-r from-orange-200 to-orange-400 text-orange-800'
  };

  return (
    <a
      href={sponsor.website_url}
      target="_blank"
      rel="noopener noreferrer sponsored"
      className="block rounded-lg border overflow-hidden hover:shadow-lg transition-shadow"
    >
      {sponsor.ad_image_url && (
        <img 
          src={sponsor.ad_image_url} 
          alt={sponsor.name}
          className="w-full h-32 object-cover"
        />
      )}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          {sponsor.logo_url && (
            <img 
              src={sponsor.logo_url} 
              alt={sponsor.name}
              className="w-8 h-8 rounded"
            />
          )}
          <span className="font-medium">{sponsor.name}</span>
          <span className={`text-xs px-2 py-0.5 rounded capitalize ${levelColors[sponsor.sponsor_level]}`}>
            {sponsor.sponsor_level}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">{sponsor.description}</p>
      </div>
    </a>
  );
};

export default SponsorBanner;
